/**
 * Utility functions for handling redirect URLs during authentication flows.
 * Stores and retrieves the intended destination URL so users can be redirected
 * back to their original destination after login and profile selection.
 */

const REDIRECT_URL_KEY = 'auth_redirect_url';
const REDIRECT_URL_PARAM = 'returnUrl';

/**
 * Stores the redirect URL in sessionStorage for retrieval after auth flow.
 * @param url The URL to redirect to after authentication
 */
export const storeRedirectUrl = (url: string): void => {
  if (typeof window === 'undefined') return;

  // Don't store auth-related pages as redirect targets
  const authPaths = [
    '/auth/',
    '/profiles/whoiswatching',
    '/profiles/manage',
    '/vipps-redirect',
  ];
  const shouldSkip = authPaths.some((path) => url.includes(path));

  if (!shouldSkip && url !== '/') {
    sessionStorage.setItem(REDIRECT_URL_KEY, url);
  }
};

/**
 * Retrieves and clears the stored redirect URL.
 * @param fallback The fallback URL if no redirect URL is stored (default: '/browse')
 * @returns The stored redirect URL or the fallback
 */
export const getAndClearRedirectUrl = (fallback = '/browse'): string => {
  if (typeof window === 'undefined') return fallback;

  const url = sessionStorage.getItem(REDIRECT_URL_KEY);
  sessionStorage.removeItem(REDIRECT_URL_KEY);

  return url ?? fallback;
};

/**
 * Gets the redirect URL from query parameters.
 * @param searchParams URLSearchParams or search string
 * @returns The returnUrl parameter value or undefined
 */
export const getReturnUrlFromParams = (
  searchParams?: URLSearchParams | string,
): string | undefined => {
  if (!searchParams) return undefined;

  const params =
    typeof searchParams === 'string'
      ? new URLSearchParams(searchParams)
      : searchParams;

  const returnUrl = params.get(REDIRECT_URL_PARAM);

  // Validate that it's a relative URL (security)
  if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
    return returnUrl;
  }

  return undefined;
};

/**
 * Appends returnUrl parameter to a URL.
 * @param baseUrl The base URL to append to
 * @param returnUrl The URL to add as returnUrl parameter
 * @returns The URL with returnUrl parameter
 */
export const appendReturnUrl = (baseUrl: string, returnUrl: string): string => {
  if (!returnUrl || returnUrl === '/') return baseUrl;

  // Don't store auth-related pages as redirect targets
  const authPaths = [
    '/auth/',
    '/profiles/whoiswatching',
    '/profiles/manage',
    '/vipps-redirect',
  ];
  const shouldSkip = authPaths.some((path) => returnUrl.includes(path));

  if (shouldSkip) return baseUrl;

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}${REDIRECT_URL_PARAM}=${encodeURIComponent(returnUrl)}`;
};

/**
 * Stores returnUrl from query params into sessionStorage for later retrieval.
 * This should be called on pages that need to pass the URL through multiple steps.
 * @param searchParams URLSearchParams or search string
 */
export const preserveReturnUrl = (
  searchParams?: URLSearchParams | string,
): void => {
  const returnUrl = getReturnUrlFromParams(searchParams);
  if (returnUrl) {
    storeRedirectUrl(returnUrl);
  }
};

export const REDIRECT_URL_PARAM_NAME = REDIRECT_URL_PARAM;
